import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Asaas } from './Asaas';
import { Valor } from '../../components/Valor';
import { useMemo, useState } from 'react';
import {
  ApiError,
  api,
  type Account,
  type CategoryNode,
  type Payable,
  type Recurrence,
  type VhJurisResult,
} from '../../lib/api';
import {
  currentMonth,
  dateBR,
  money,
  monthLabel,
  parseAmountInput,
  shiftMonth,
  today,
} from '../../lib/format';

/**
 * Contas a pagar.
 *
 * O sistema nao paga nada, e a tela nao finge que paga: o botao se chama
 * "Dar baixa", e a linha digitavel fica a um clique de ser copiada para quem
 * vai pagar no aplicativo do banco.
 */
export function PayablesPage() {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(currentMonth());
  const [mode, setMode] = useState<'none' | 'payable' | 'recurrence'>('none');
  /** De que lado e o compromisso que esta sendo cadastrado. */
  const [lado, setLado] = useState<'INCOME' | 'EXPENSE'>('EXPENSE');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [paying, setPaying] = useState<Payable | null>(null);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.categories() });
  const recurrences = useQuery({ queryKey: ['recurrences'], queryFn: () => api.recurrences() });

  const payables = useQuery({
    queryKey: ['payables', month],
    queryFn: () => api.payables(`?month=${month}`),
  });

  /** O lado do compromisso manda na lista: ganho fixo so aceita entrada. */
  const categoriasDoLado = useMemo(
    () => flatten(categories.data?.categories ?? []).filter((node) => node.kind === lado),
    [categories.data, lado],
  );

  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['payables'] });
    void queryClient.invalidateQueries({ queryKey: ['compromissos'] });
    void queryClient.invalidateQueries({ queryKey: ['recurrences'] });
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const fail = (caught: unknown) => setError(messageFor(caught));

  const generate = useMutation({
    mutationFn: () => api.generatePayables(3),
    onSuccess: (result) => {
      setError(null);
      setNotice(
        result.created === 0
          ? 'Nenhum vencimento novo: os proximos tres meses ja estavam gerados.'
          : `${result.created} vencimento(s) gerado(s) para os proximos tres meses.`,
      );
      refreshAll();
    },
    onError: fail,
  });

  const unpay = useMutation({
    mutationFn: (id: string) => api.unpayPayable(id),
    onSuccess: refreshAll,
    onError: fail,
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelPayable(id),
    onSuccess: refreshAll,
    onError: fail,
  });

  const resumo = useQuery({
    queryKey: ['compromissos', month],
    queryFn: () => api.compromissos(month),
  });

  const conciliar = useMutation({
    mutationFn: () => api.conciliarCompromissos(),
    onSuccess: ({ baixados }) => {
      setNotice(
        baixados === 0
          ? 'Nenhum compromisso encontrou pagamento no extrato ainda.'
          : baixados === 1
            ? '1 compromisso recebeu baixa pelo extrato.'
            : `${baixados} compromissos receberam baixa pelo extrato.`,
      );
      refreshAll();
    },
    onError: fail,
  });

  return (
    <div className="main">
      <header className="page-header">
        <div className="between">
          <div>
            <h1>Compromissos</h1>
            <p>
              O que entra todo mes e o que sai todo mes. Quando o pagamento aparece no extrato, a
              baixa acontece sozinha — o sistema nao paga nada e nao lanca nada em dobro.
            </p>
          </div>
          <div className="row">
            <button
              className="btn"
              onClick={() => {
                setLado('EXPENSE');
                setMode(mode === 'payable' && lado === 'EXPENSE' ? 'none' : 'payable');
              }}
            >
              {mode === 'payable' && lado === 'EXPENSE' ? 'Cancelar' : 'Nova conta'}
            </button>
            <button
              className="btn"
              onClick={() => {
                setLado('INCOME');
                setMode(mode === 'payable' && lado === 'INCOME' ? 'none' : 'payable');
              }}
            >
              {mode === 'payable' && lado === 'INCOME' ? 'Cancelar' : 'Novo ganho'}
            </button>
            <button
              className="btn secondary"
              onClick={() => setMode(mode === 'recurrence' ? 'none' : 'recurrence')}
            >
              {mode === 'recurrence' ? 'Cancelar' : 'Todo mes'}
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}

      {notice && (
        <div className="alert success" role="status">
          {notice}
        </div>
      )}

      {mode === 'payable' && (
        <PayableForm
          accounts={accounts.data?.accounts ?? []}
          categories={categoriasDoLado}
          kind={lado}
          onDone={(created) => {
            setMode('none');
            setError(null);
            // Uma conta cadastrada para o mes que vem sumiria da tela sem
            // explicacao, porque a lista mostra um mes por vez. Ir junto com
            // ela e o minimo para a pessoa ver o que acabou de criar.
            setMonth(created.dueDate.slice(0, 7));
            refreshAll();
          }}
          onError={setError}
        />
      )}

      {mode === 'recurrence' && (
        <RecurrenceForm
          accounts={accounts.data?.accounts ?? []}
          categories={categoriasDoLado}
          kind={lado}
          onLado={setLado}
          onDone={() => {
            setMode('none');
            setError(null);
            refreshAll();
          }}
          onError={setError}
        />
      )}

      {paying && (
        <PayForm
          payable={paying}
          accounts={accounts.data?.accounts ?? []}
          onDone={() => {
            setPaying(null);
            setError(null);
            refreshAll();
          }}
          onCancel={() => setPaying(null)}
          onError={setError}
        />
      )}

      {resumo.data && (
        <>
          <div className="grid">
            <article className="stat destaque">
              <div className="stat-label">Livre para usar</div>
              <Valor cents={resumo.data.sobraCents} className="stat-value" />
              <div className="card-hint" style={{ margin: 0 }}>
                o que ha em conta, mais o que ainda entra, menos tudo o que ainda sai
              </div>
            </article>
            <article className="stat">
              <div className="stat-label">Ainda entra</div>
              <Valor cents={resumo.data.aReceberCents} className="stat-value" />
            </article>
            <article className="stat">
              <div className="stat-label">Ainda sai</div>
              <Valor cents={resumo.data.aPagarCents} saida className="stat-value" />
            </article>
            <article className="stat">
              <div className="stat-label">Fatura do cartao</div>
              <Valor cents={resumo.data.faturaCents} saida className="stat-value" />
            </article>
          </div>

          {BigInt(resumo.data.atrasadoCents) > 0n && (
            <div className="alert error" role="alert" style={{ marginTop: 'var(--space-4)' }}>
              Ha {money(resumo.data.atrasadoCents)} vencido e ainda em aberto.
            </div>
          )}
        </>
      )}

      <Asaas />

      <Honorarios />

      <section className="card" style={{ marginTop: 'var(--space-6)' }}>
        <div className="between" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 className="card-title" style={{ marginBottom: 0 }}>
            {monthLabel(month)}
          </h2>
          <div className="row">
            {/* A baixa ja roda sozinha ao fim de cada varredura. O botao
                existe para quem acabou de cadastrar e nao quer esperar. */}
            <button
              className="btn ghost"
              disabled={conciliar.isPending}
              onClick={() => conciliar.mutate()}
              title="Procura no extrato o pagamento de cada compromisso em aberto"
            >
              {conciliar.isPending ? 'Conferindo...' : 'Conferir no extrato'}
            </button>
            <button className="btn ghost" onClick={() => setMonth(shiftMonth(month, -1))}>
              Mes anterior
            </button>
            <button className="btn ghost" onClick={() => setMonth(shiftMonth(month, 1))}>
              Proximo mes
            </button>
          </div>
        </div>

        {(payables.data?.payables ?? []).length === 0 ? (
          <p className="card-hint" style={{ marginBottom: 0 }}>
            Nada a pagar neste mes.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Vencimento</th>
                  <th>Conta</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th>Situacao</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(payables.data?.payables ?? []).map((payable) => (
                  <tr key={payable.id}>
                    <td className="tabular">{dateBR(payable.dueDate)}</td>
                    <td>
                      {payable.description}
                      <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                        {[
                          payable.kind === 'INCOME' ? 'ganho' : null,
                          payable.categoryName,
                          payable.recurrenceDescription && 'todo mes',
                          payable.counterpartyMatch
                            ? `baixa por "${payable.counterpartyMatch}"`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                      {payable.boletoFormattedLine && (
                        <CopyLine
                          line={payable.boletoLine ?? ''}
                          formatted={payable.boletoFormattedLine}
                          bank={payable.boletoBankName}
                        />
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Valor
                        cents={payable.paidAmountCents ?? payable.amountCents}
                        saida={payable.kind === 'EXPENSE'}
                      />
                    </td>
                    <td>
                      <SituationBadge payable={payable} />
                    </td>
                    <td>
                      <div className="row">
                        {payable.status === 'OPEN' && (
                          <>
                            <button className="btn" onClick={() => setPaying(payable)}>
                              Dar baixa
                            </button>
                            <button className="btn ghost" onClick={() => cancel.mutate(payable.id)}>
                              Cancelar
                            </button>
                          </>
                        )}
                        {payable.status === 'PAID' && (
                          <button className="btn ghost" onClick={() => unpay.mutate(payable.id)}>
                            Desfazer baixa
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <div className="between" style={{ marginBottom: 'var(--space-4)' }}>
          <div>
            <h2 className="card-title">O que se repete</h2>
            <p className="card-hint" style={{ marginBottom: 0 }}>
              Contas fixas e ganhos periodicos. A serie guarda a regra; os vencimentos nascem ate
              tres meses a frente, sozinhos. Gerar duas vezes nao duplica nada.
            </p>
          </div>
          <button
            className="btn secondary"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
          >
            {generate.isPending ? 'Gerando...' : 'Gerar vencimentos'}
          </button>
        </div>

        {(recurrences.data?.recurrences ?? []).length === 0 ? (
          <p className="card-hint" style={{ marginBottom: 0 }}>
            Nada cadastrado ainda. Um contrato de honorarios em doze vezes, o aluguel, o salario:
            tudo o que tem data e valor esperado cabe aqui.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Descricao</th>
                  <th>Quando</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th>Dia / proximo</th>
                  <th style={{ textAlign: 'right' }}>Geradas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(recurrences.data?.recurrences ?? []).map((recurrence) => (
                  <RecurrenceRow key={recurrence.id} recurrence={recurrence} onDone={refreshAll} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const FREQUENCY_LABELS: Record<Recurrence['frequency'], string> = {
  WEEKLY: 'Toda semana',
  MONTHLY: 'Todo mes',
  QUARTERLY: 'A cada tres meses',
  YEARLY: 'Todo ano',
};

/**
 * Os honorarios que o VH JURIS ja baixou.
 *
 * Nao ha lista aqui nem botao de lancar: os honorarios aparecem no extrato
 * como qualquer outra entrada, trazidos pelo banco, e o que esta ligacao faz e
 * dizer quais creditos sao honorario. Lancar por aqui criaria o mesmo dinheiro
 * duas vezes.
 *
 * O unico numero que pede acao e "pendentes": honorario que o escritorio deu
 * por recebido e que nao apareceu em banco nenhum — dinheiro em especie, ou em
 * conta que nao esta conectada.
 */
function Honorarios() {
  const cliente = useQueryClient();
  const estado = useQuery({ queryKey: ['vh-juris'], queryFn: () => api.vhJuris() });
  const [ultimo, setUltimo] = useState<VhJurisResult | null>(null);

  const sincronizar = useMutation({
    mutationFn: () => api.sincronizarVhJuris(),
    onSuccess: (resultado) => {
      setUltimo(resultado);
      void cliente.invalidateQueries({ queryKey: ['vh-juris'] });
      void cliente.invalidateQueries({ queryKey: ['payables'] });
      void cliente.invalidateQueries({ queryKey: ['compromissos'] });
      void cliente.invalidateQueries({ queryKey: ['transactions'] });
    },
  });

  // Desligada no servidor: nao ha o que mostrar nem o que explicar aqui.
  if (!estado.data?.configurado) return null;

  return (
    <section className="card" style={{ marginTop: 'var(--space-6)' }}>
      <div className="between">
        <h2 className="card-title" style={{ marginBottom: 0 }}>
          Honorarios do VH JURIS
        </h2>
        <button
          className="btn ghost"
          disabled={sincronizar.isPending}
          onClick={() => sincronizar.mutate()}
          title="Le as baixas do VH JURIS e procura o credito correspondente no extrato"
        >
          {sincronizar.isPending ? 'Lendo...' : 'Ler agora'}
        </button>
      </div>

      <p className="card-hint">
        Cada baixa la vira a procura de um credito aqui. Quando o credito e encontrado, ele e
        marcado como honorario — nenhum lancamento e criado, para o mesmo dinheiro nao contar duas
        vezes. A leitura tambem acontece sozinha ao fim de cada varredura.
      </p>

      <div className="row" style={{ gap: 'var(--space-4)' }}>
        <span className="muted">
          {estado.data.total === 1
            ? '1 recebimento lido'
            : `${estado.data.total} recebimentos lidos`}
        </span>
        {estado.data.pendentes > 0 && (
          <span>
            {estado.data.pendentes === 1
              ? '1 sem correspondencia no extrato'
              : `${estado.data.pendentes} sem correspondencia no extrato`}
          </span>
        )}
      </div>

      {estado.data.conferir.length > 0 && (
        <>
          <p className="card-hint" style={{ marginTop: 'var(--space-3)' }}>
            Estes casaram pelo valor, e quem mandou o dinheiro tem outro nome. Costuma ser parente
            pagando pelo cliente — mas quem confirma e voce.
          </p>
          <ul className="leitura-linhas">
            {estado.data.conferir.map((linha) => (
              <li key={linha.id} className="leitura-linha">
                <span className="leitura-linha-dia">
                  {linha.paidOn ? dateBR(linha.paidOn) : ''}
                </span>
                <span className="leitura-linha-nome">
                  {linha.description}
                  <small>pago por {linha.quemPagou}</small>
                </span>
                <Valor cents={linha.amountCents} className="leitura-linha-valor" />
              </li>
            ))}
          </ul>
        </>
      )}

      {ultimo?.erro && <p className="card-hint">{ultimo.erro}</p>}
      {ultimo && !ultimo.erro && (
        <p className="card-hint" style={{ marginBottom: 0 }}>
          {ultimo.novos} novo(s), {ultimo.baixados} encontrado(s) no extrato
          {ultimo.removidos > 0 ? `, ${ultimo.removidos} removido(s)` : ''}.
        </p>
      )}
    </section>
  );
}

function SituationBadge({ payable }: { payable: Payable }) {
  if (payable.status === 'PAID') {
    return (
      <span className="income">Paga{payable.paidOn ? ` em ${dateBR(payable.paidOn)}` : ''}</span>
    );
  }
  if (payable.status === 'CANCELED') return <span className="muted">Cancelada</span>;

  if (payable.overdue) {
    const dias = Math.abs(payable.daysUntilDue);
    return <span className="expense">Atrasada ha {dias} dia(s)</span>;
  }

  if (payable.daysUntilDue === 0) return <span>Vence hoje</span>;
  return <span className="muted">Em {payable.daysUntilDue} dia(s)</span>;
}

/**
 * Copiar a linha digitavel e o que a pessoa realmente vai fazer com o boleto
 * aqui dentro — o pagamento acontece no aplicativo do banco dela.
 */
function CopyLine({
  line,
  formatted,
  bank,
}: {
  line: string;
  formatted: string;
  bank: string | null;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div style={{ marginTop: 4 }}>
      <button
        className="btn ghost"
        style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
        onClick={() => {
          void navigator.clipboard?.writeText(line);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
        }}
        aria-label={`Copiar linha digitavel${bank ? ` do ${bank}` : ''}`}
      >
        {copied ? 'Copiada' : 'Copiar linha digitavel'}
      </button>
      <div className="muted tabular" style={{ fontSize: 'var(--text-xs)' }}>
        {bank ? `${bank} · ` : ''}
        {formatted}
      </div>
    </div>
  );
}

function RecurrenceRow({ recurrence, onDone }: { recurrence: Recurrence; onDone: () => void }) {
  const [dia, setDia] = useState(String(Number(recurrence.startDate.slice(8, 10))));
  const [editandoValor, setEditandoValor] = useState(false);
  const [valor, setValor] = useState(() => centavosParaTexto(recurrence.amountCents));

  /**
   * O valor da serie, corrigivel na propria linha.
   *
   * O valor e previsao, e previsao envelhece: a luz sobe, o plano reajusta, o
   * aluguel muda no aniversario do contrato. Deixar o sistema ajustar sozinho
   * pela ultima baixa seria pratico e perigoso — um mes atipico viraria a nova
   * previsao sem ninguem decidir isso.
   *
   * Entao quem corrige e voce, aqui. A baixa continua acontecendo pelo nome, e
   * nao pelo valor, entao errar a previsao nunca impede a conta de ser
   * reconhecida: so deixa a projecao do mes menos precisa.
   */
  const mudarValor = useMutation({
    mutationFn: (centavos: bigint) =>
      api.updateRecurrence(recurrence.id, { amountCents: centavos.toString() }),
    onSuccess: () => {
      setEditandoValor(false);
      onDone();
    },
  });

  const salvarValor = () => {
    try {
      const centavos = parseAmountInput(valor);
      if (centavos <= 0n || centavos.toString() === recurrence.amountCents) {
        setEditandoValor(false);
        setValor(centavosParaTexto(recurrence.amountCents));
        return;
      }
      mudarValor.mutate(centavos);
    } catch {
      setEditandoValor(false);
      setValor(centavosParaTexto(recurrence.amountCents));
    }
  };

  /**
   * O dia em que o dinheiro se move, corrigivel na propria linha.
   *
   * Ninguem sabe de cor: a folha cai no ultimo dia util, o boleto vence no
   * dia 10 e e debitado no 12. A pessoa cadastra o dia que imagina e so
   * descobre o certo quando ve a baixa acontecer — e o dia errado nao e
   * cosmetico: ele decide em que mes a previsao aparece.
   */
  const mudarDia = useMutation({
    mutationFn: (novo: number) =>
      api.updateRecurrence(recurrence.id, {
        startDate: `${recurrence.startDate.slice(0, 8)}${String(novo).padStart(2, '0')}`,
      }),
    onSuccess: onDone,
  });

  const toggle = useMutation({
    mutationFn: () => api.updateRecurrence(recurrence.id, { active: !recurrence.active }),
    onSuccess: onDone,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteRecurrence(recurrence.id),
    onSuccess: onDone,
  });

  return (
    <tr style={{ opacity: recurrence.active ? 1 : 0.5 }}>
      <td>
        {recurrence.description}
        <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          {[
            recurrence.kind === 'INCOME' ? 'ganho fixo' : 'conta fixa',
            recurrence.categoryName,
            recurrence.counterpartyMatch ? `baixa por "${recurrence.counterpartyMatch}"` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </td>
      <td className="muted">
        {FREQUENCY_LABELS[recurrence.frequency]}
        <div style={{ fontSize: 'var(--text-xs)' }}>{prazoDaSerie(recurrence)}</div>
      </td>
      <td style={{ textAlign: 'right' }}>
        {editandoValor ? (
          <input
            className="edit-inline tabular"
            style={{ width: 110, minWidth: 0, textAlign: 'right' }}
            value={valor}
            autoFocus
            disabled={mudarValor.isPending}
            onChange={(event) => setValor(event.target.value)}
            onBlur={salvarValor}
            onKeyDown={(event) => {
              if (event.key === 'Enter') salvarValor();
              if (event.key === 'Escape') {
                setValor(centavosParaTexto(recurrence.amountCents));
                setEditandoValor(false);
              }
            }}
          />
        ) : (
          <button
            className="valor-editavel"
            onClick={() => setEditandoValor(true)}
            title="Clique para corrigir o valor previsto desta serie"
          >
            <Valor cents={recurrence.amountCents} saida={recurrence.kind === 'EXPENSE'} />
          </button>
        )}
      </td>
      <td className="tabular">
        <span className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
          <label
            className="muted"
            style={{ fontSize: 'var(--text-xs)' }}
            htmlFor={`dia-${recurrence.id}`}
          >
            dia
          </label>
          <input
            id={`dia-${recurrence.id}`}
            type="number"
            min={1}
            max={31}
            value={dia}
            disabled={mudarDia.isPending}
            onChange={(event) => setDia(event.target.value)}
            onBlur={() => {
              const novo = Number(dia);
              if (!novo || novo < 1 || novo > 31) return;
              if (novo === Number(recurrence.startDate.slice(8, 10))) return;
              mudarDia.mutate(novo);
            }}
            style={{ width: 62 }}
          />
        </span>
        <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          {recurrence.nextDueDate ? dateBR(recurrence.nextDueDate) : '—'}
        </div>
      </td>
      <td className="tabular" style={{ textAlign: 'right' }}>
        {recurrence.occurrences
          ? `${recurrence.generatedCount} de ${recurrence.occurrences}`
          : recurrence.generatedCount}
      </td>
      <td>
        <div className="row">
          <button className="btn ghost" onClick={() => toggle.mutate()}>
            {recurrence.active ? 'Pausar' : 'Retomar'}
          </button>
          <button className="btn danger" onClick={() => remove.mutate()}>
            Encerrar
          </button>
        </div>
      </td>
    </tr>
  );
}

// ------------------------------------------------------------- formularios

interface FlatCategory {
  id: string;
  path: string;
  kind: 'INCOME' | 'EXPENSE';
}

function PayableForm({
  accounts,
  categories,
  kind,
  onDone,
  onError,
}: {
  accounts: Account[];
  categories: FlatCategory[];
  kind: 'INCOME' | 'EXPENSE';
  onDone: (created: Payable) => void;
  onError: (message: string) => void;
}) {
  const entrada = kind === 'INCOME';
  const [description, setDescription] = useState('');
  const [counterpartyMatch, setCounterpartyMatch] = useState('');
  const [dueDate, setDueDate] = useState(today());
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [boletoCode, setBoletoCode] = useState('');
  const [boletoInfo, setBoletoInfo] = useState<string | null>(null);

  /**
   * Ler o boleto preenche o formulario, e nao grava nada. O valor e a data
   * continuam editaveis: a arrecadacao nao traz vencimento, e o titulo pode
   * ter juros que o papel nao mostra.
   */
  const read = useMutation({
    mutationFn: (code: string) => api.readBoleto(code),
    onSuccess: (boleto) => {
      if (boleto.amountCents)
        setAmount((Number(boleto.amountCents) / 100).toFixed(2).replace('.', ','));
      if (boleto.dueDate) setDueDate(boleto.dueDate);
      setBoletoInfo(
        [
          boleto.bankName ?? boleto.segmentName ?? 'Boleto reconhecido',
          boleto.dueDate ? null : 'este tipo de boleto nao traz vencimento: informe a data',
          boleto.dueDateNote,
          boleto.amountNote,
        ]
          .filter(Boolean)
          .join(' — '),
      );
    },
    onError: (caught) => onError(messageFor(caught)),
  });

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.createPayable(payload),
    onSuccess: onDone,
    onError: (caught) => onError(messageFor(caught)),
  });

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    try {
      create.mutate({
        description,
        dueDate,
        kind,
        amountCents: parseAmountInput(amount).toString(),
        ...(counterpartyMatch.trim() ? { counterpartyMatch: counterpartyMatch.trim() } : {}),
        ...(categoryId ? { categoryId } : {}),
        ...(accountId ? { accountId } : {}),
        ...(boletoCode ? { boletoCode } : {}),
      });
    } catch {
      onError('Valor invalido. Use o formato 1.234,56.');
    }
  };

  return (
    <section className="card">
      <h2 className="card-title">{entrada ? 'Novo ganho fixo' : 'Nova conta a pagar'}</h2>
      <p className="card-hint">
        {entrada
          ? 'O que voce espera receber: salario, honorario recorrente, aluguel. Quando cair na conta, o sistema reconhece e da baixa sozinho.'
          : 'O que voce vai pagar. Quando sair da conta, o sistema reconhece no extrato e da baixa sozinho — sem criar um segundo lancamento.'}
      </p>

      <div className="field" style={entrada ? { display: 'none' } : undefined}>
        <label htmlFor="boletoCode">Boleto (opcional)</label>
        <div className="row">
          <input
            id="boletoCode"
            value={boletoCode}
            onChange={(event) => setBoletoCode(event.target.value)}
            placeholder="Cole a linha digitavel"
            style={{ flex: 1, minWidth: 260 }}
          />
          <button
            type="button"
            className="btn secondary"
            onClick={() => read.mutate(boletoCode)}
            disabled={boletoCode.trim().length < 30 || read.isPending}
          >
            {read.isPending ? 'Lendo...' : 'Ler boleto'}
          </button>
        </div>
        {boletoInfo && <span className="field-hint">{boletoInfo}</span>}
      </div>

      <form onSubmit={submit}>
        <div className="row">
          <div className="field" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="payableDescription">Descricao</label>
            <input
              id="payableDescription"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              required
            />
          </div>

          <div className="field" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="payableMatch">Como aparece no extrato</label>
            <input
              id="payableMatch"
              value={counterpartyMatch}
              onChange={(event) => setCounterpartyMatch(event.target.value)}
              placeholder={entrada ? 'MUNICIPIO DE...' : 'LIGHT SERVICOS...'}
            />
            {/*
              O campo que faz a baixa acontecer sozinha — e o unico da tela
              que pede um texto do BANCO, nao seu. Casar por nome e muito mais
              firme que casar por valor: a conta de luz muda todo mes, o nome
              da concessionaria nao.
            */}
            <span className="field-hint">
              Parte do nome de quem {entrada ? 'paga' : 'recebe'} — e o que identifica o pagamento,
              mesmo que o valor e o dia mudem. Em branco, a baixa so acontece com o valor exato.
            </span>
          </div>

          <div className="field" style={{ width: 170 }}>
            <label htmlFor="payableDueDate">Vencimento</label>
            <input
              id="payableDueDate"
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              required
            />
          </div>

          <div className="field" style={{ width: 150 }}>
            <label htmlFor="payableAmount">Valor</label>
            <input
              id="payableAmount"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="185,00"
              required
            />
          </div>
        </div>

        <div className="row">
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label htmlFor="payableCategory">Categoria</label>
            <select
              id="payableCategory"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">Sem categoria</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.path}
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label htmlFor="payableAccount">Conta prevista</label>
            <select
              id="payableAccount"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
            >
              <option value="">Decidir na hora</option>
              {accounts
                .filter((account) => !account.archived)
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <button className="btn" type="submit" disabled={create.isPending}>
          {create.isPending ? 'Salvando...' : 'Cadastrar'}
        </button>
      </form>
    </section>
  );
}

function PayForm({
  payable,
  accounts,
  onDone,
  onCancel,
  onError,
}: {
  payable: Payable;
  accounts: Account[];
  onDone: () => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const [accountId, setAccountId] = useState(payable.accountId ?? '');
  const [paidOn, setPaidOn] = useState(today());
  const [amount, setAmount] = useState(
    (Number(payable.amountCents) / 100).toFixed(2).replace('.', ','),
  );

  const pay = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.payPayable(payable.id, payload),
    onSuccess: onDone,
    onError: (caught) => onError(messageFor(caught)),
  });

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    try {
      pay.mutate({ accountId, paidOn, amountCents: parseAmountInput(amount).toString() });
    } catch {
      onError('Valor invalido. Use o formato 1.234,56.');
    }
  };

  return (
    <section className="card">
      <h2 className="card-title">Dar baixa em {payable.description}</h2>
      <p className="card-hint">
        Isto nao paga a conta: registra que voce pagou e cria o lancamento correspondente.
      </p>

      <form onSubmit={submit}>
        <div className="row">
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label htmlFor="payAccount">Pago com</label>
            <select
              id="payAccount"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
              required
            >
              <option value="">Escolha a conta</option>
              {accounts
                .filter((account) => !account.archived)
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="field" style={{ width: 170 }}>
            <label htmlFor="payDate">Pago em</label>
            <input
              id="payDate"
              type="date"
              value={paidOn}
              onChange={(event) => setPaidOn(event.target.value)}
              required
            />
          </div>

          <div className="field" style={{ width: 150 }}>
            <label htmlFor="payAmount">Valor pago</label>
            <input
              id="payAmount"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              required
            />
            <span className="field-hint">Pode diferir do previsto.</span>
          </div>
        </div>

        <div className="row">
          <button className="btn" type="submit" disabled={pay.isPending}>
            {pay.isPending ? 'Registrando...' : 'Confirmar baixa'}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel}>
            Voltar
          </button>
        </div>
      </form>
    </section>
  );
}

function RecurrenceForm({
  accounts,
  categories,
  kind,
  onLado,
  onDone,
  onError,
}: {
  accounts: Account[];
  categories: FlatCategory[];
  kind: 'INCOME' | 'EXPENSE';
  onLado: (lado: 'INCOME' | 'EXPENSE') => void;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [description, setDescription] = useState('');
  const [counterpartyMatch, setCounterpartyMatch] = useState('');
  const [frequency, setFrequency] = useState<Recurrence['frequency']>('MONTHLY');
  const [amount, setAmount] = useState('');
  const [startDate, setStartDate] = useState(today());
  /**
   * Ate quando a serie vai — em tres formas, e nunca em duas ao mesmo tempo.
   *
   * O servidor recusa data final E numero de parcelas juntos, porque as duas
   * responderiam a mesma pergunta e poderiam discordar. Escolher a forma
   * antes de preencher e o que impede a pessoa de descobrir isso por um erro
   * depois de digitar tudo.
   */
  const [prazo, setPrazo] = useState<'aberto' | 'ate' | 'parcelas'>('aberto');
  const [endDate, setEndDate] = useState('');
  const [occurrences, setOccurrences] = useState('12');
  const [categoryId, setCategoryId] = useState('');
  const [accountId, setAccountId] = useState('');

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.createRecurrence(payload),
    onSuccess: onDone,
    onError: (caught) => onError(messageFor(caught)),
  });

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    try {
      create.mutate({
        description,
        frequency,
        kind,
        amountCents: parseAmountInput(amount).toString(),
        startDate,
        ...(prazo === 'ate' && endDate ? { endDate } : {}),
        ...(prazo === 'parcelas' && occurrences ? { occurrences: Number(occurrences) } : {}),
        ...(counterpartyMatch.trim() ? { counterpartyMatch: counterpartyMatch.trim() } : {}),
        ...(categoryId ? { categoryId } : {}),
        ...(accountId ? { accountId } : {}),
      });
    } catch {
      onError('Valor invalido. Use o formato 1.234,56.');
    }
  };

  return (
    <section className="card">
      <h2 className="card-title">O que se repete todo mes</h2>
      <p className="card-hint">
        Cadastre uma vez e os vencimentos nascem sozinhos, ate tres meses a frente. O dia do
        primeiro vencimento vira o dia da serie: dia 31 encolhe para o ultimo dia nos meses curtos e
        volta a 31 no mes seguinte.
      </p>

      <form onSubmit={submit}>
        <div className="row">
          <div className="field" style={{ width: 200 }}>
            <label htmlFor="recurrenceKind">O que e</label>
            <select
              id="recurrenceKind"
              value={kind}
              onChange={(event) => onLado(event.target.value as 'INCOME' | 'EXPENSE')}
            >
              <option value="EXPENSE">Conta fixa (sai)</option>
              <option value="INCOME">Ganho fixo (entra)</option>
            </select>
          </div>

          <div className="field" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="recurrenceDescription">Descricao</label>
            <input
              id="recurrenceDescription"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={kind === 'INCOME' ? 'Salario' : 'Aluguel'}
              required
            />
          </div>

          <div className="field" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="recurrenceMatch">Como aparece no extrato</label>
            <input
              id="recurrenceMatch"
              value={counterpartyMatch}
              onChange={(event) => setCounterpartyMatch(event.target.value)}
              placeholder={kind === 'INCOME' ? 'MUNICIPIO DE...' : 'LIGHT SERVICOS...'}
            />
            <span className="field-hint">
              Parte do nome de quem {kind === 'INCOME' ? 'paga' : 'recebe'}. E o que identifica o
              pagamento — a baixa acontece mesmo que o valor e o dia mudem.
            </span>
          </div>

          <div className="field" style={{ width: 180 }}>
            <label htmlFor="recurrenceFrequency">Repete</label>
            <select
              id="recurrenceFrequency"
              value={frequency}
              onChange={(event) => setFrequency(event.target.value as Recurrence['frequency'])}
            >
              <option value="MONTHLY">Todo mes</option>
              <option value="WEEKLY">Toda semana</option>
              <option value="QUARTERLY">A cada tres meses</option>
              <option value="YEARLY">Todo ano</option>
            </select>
          </div>

          <div className="field" style={{ width: 150 }}>
            <label htmlFor="recurrenceAmount">Valor</label>
            <input
              id="recurrenceAmount"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="1.800,00"
              required
            />
          </div>

          <div className="field" style={{ width: 170 }}>
            <label htmlFor="recurrenceStart">Primeiro vencimento</label>
            <input
              id="recurrenceStart"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              required
            />
            <span className="field-hint">
              O dia daqui vira o dia da serie — e ele so diz quando esperar. Quem identifica o
              pagamento e o nome acima, entao o dia nao precisa estar certo.
            </span>
          </div>

          <div className="field" style={{ width: 180 }}>
            <label htmlFor="recurrencePrazo">Ate quando</label>
            <select
              id="recurrencePrazo"
              value={prazo}
              onChange={(event) => setPrazo(event.target.value as typeof prazo)}
            >
              <option value="aberto">Sem prazo</option>
              <option value="parcelas">Numero de parcelas</option>
              <option value="ate">Ate uma data</option>
            </select>
          </div>

          {prazo === 'parcelas' && (
            <div className="field" style={{ width: 140 }}>
              <label htmlFor="recurrenceOccurrences">Parcelas</label>
              <input
                id="recurrenceOccurrences"
                type="number"
                min={1}
                max={600}
                value={occurrences}
                onChange={(event) => setOccurrences(event.target.value)}
                required
              />
              {/* Conta desde o primeiro vencimento, e nao desde hoje: e assim
                  que um contrato de honorarios em doze vezes e escrito. */}
              <span className="field-hint">Contando o primeiro.</span>
            </div>
          )}

          {prazo === 'ate' && (
            <div className="field" style={{ width: 170 }}>
              <label htmlFor="recurrenceEnd">Ultimo vencimento</label>
              <input
                id="recurrenceEnd"
                type="date"
                value={endDate}
                min={startDate}
                onChange={(event) => setEndDate(event.target.value)}
                required
              />
            </div>
          )}
        </div>

        <div className="row">
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label htmlFor="recurrenceCategory">Categoria</label>
            <select
              id="recurrenceCategory"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">Sem categoria</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.path}
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label htmlFor="recurrenceAccount">Conta prevista</label>
            <select
              id="recurrenceAccount"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
            >
              <option value="">Decidir na hora</option>
              {accounts
                .filter((account) => !account.archived)
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <button className="btn" type="submit" disabled={create.isPending}>
          {create.isPending ? 'Salvando...' : 'Criar conta fixa'}
        </button>
      </form>
    </section>
  );
}

/**
 * Ate quando a serie vai, em uma linha.
 *
 * Sem isto a tela nao distingue um aluguel — que nao acaba — de um contrato
 * de honorarios em doze vezes, que acaba. Sao a mesma linha na tabela e
 * significam coisas muito diferentes para quem planeja o ano.
 */
function prazoDaSerie(recurrence: Recurrence): string {
  if (recurrence.occurrences) {
    const faltam = recurrence.occurrences - recurrence.generatedCount;
    if (faltam <= 0) return `${recurrence.occurrences} parcelas, serie completa`;
    return `${recurrence.occurrences} parcelas`;
  }

  if (recurrence.endDate) return `ate ${dateBR(recurrence.endDate)}`;
  return 'sem prazo';
}

function flatten(nodes: CategoryNode[], prefix = ''): FlatCategory[] {
  return nodes.flatMap((node) => {
    const path = prefix ? `${prefix} / ${node.name}` : node.name;
    return [{ id: node.id, path, kind: node.kind }, ...flatten(node.children, path)];
  });
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) return caught.message;
  return 'Nao consegui concluir. Tente de novo.';
}

/** Centavos inteiros viram o texto que a pessoa digitaria: "1.234,56". */
function centavosParaTexto(cents: string): string {
  const valor = BigInt(cents);
  const negativo = valor < 0n;
  const absoluto = negativo ? -valor : valor;
  const reais = absoluto / 100n;
  const resto = absoluto % 100n;
  return `${negativo ? '-' : ''}${reais.toString()},${resto.toString().padStart(2, '0')}`;
}
